import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils/utils';
import { PageHeader } from '@/pages/_partials/mobile-headers';
import { BackButton } from '@/components/ui/app/back-button';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { Shield, Loader2, ChevronDown, Info } from 'lucide-react';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { useAuth } from '@/lib/contexts/auth-context';
import { MobileHeaderNavigation } from '@/pages/_partials/mobile-headers';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';

/* ─── Constantes ──────────────────────────────────────────── */

function getRoles() {
    return [
        { key: 'admin_col', label: i18n.t('permissions:admin'), apiRole: 'owner' },
        { key: 'manager_col', label: i18n.t('permissions:manager'), apiRole: 'head_department' },
        { key: 'member_col', label: i18n.t('permissions:membre'), apiRole: 'employee' },
    ];
}

/** Rôles visibles selon le rôle de l'utilisateur connecté */
const VISIBLE_API_ROLES = {
    admin: ['owner', 'head_department', 'employee'],
    owner: ['head_department', 'employee'],
    head_department: ['employee'],
};

function getSelfPermissionGroups() {
    return [
        {
            theme: 'Photos',
            permissions: [
                { id: 'capture_photos', label: i18n.t('permissions:capturerDesPhotos'), apiCode: 'CAPTURE_CERTIFIED_PHOTOS' },
                { id: 'move_photo', label: i18n.t('permissions:deplacerUnePhoto'), apiCode: 'MOVE_CERTIFIED_PHOTOS' },
            ],
        },
        {
            theme: i18n.t('permissions:procedures'),
            permissions: [
                {
                    id: 'manage_procedures',
                    label: i18n.t('permissions:gererDesProceduresCreationPartageArchivage'),
                    apiCode: 'UPDATE_PROCEDURES',
                },
                {
                    id: 'modify_other_member_procedure',
                    label: i18n.t('permissions:modifierLaProcedureDUnAutreMembre'),
                    apiCode: 'UPDATE_ANOTHER_MEMBER_PROCEDURE',
                },
                { id: 'complete_procedure', label: i18n.t('permissions:completerUneProcedure'), apiCode: 'UPDATE_PROCEDURE_REPORTS' },
                { id: 'move_report', label: i18n.t('permissions:deplacerUnRapport'), apiCode: 'MOVE_PROCEDURE_REPORTS' },
            ],
        },
        {
            theme: 'Dossiers',
            permissions: [
                {
                    id: 'view_all_folders',
                    label: i18n.t('permissions:consulterTousLesDossiers'),
                    description: i18n.t('permissions:sinonLUtilisateurNeVoitQueLesDossiers'),
                    apiCode: 'LIST_FOLDERS',
                },
                { id: 'add_folders', label: i18n.t('permissions:ajouterDesDossiers'), apiCode: 'CREATE_FOLDERS' },
                { id: 'edit_folders', label: i18n.t('permissions:modifierDesDossiers'), apiCode: 'UPDATE_FOLDERS' },
                {
                    id: 'edit_folder_managers',
                    label: i18n.t('permissions:modifierLesResponsablesDeDossier'),
                    apiCode: 'UPDATE_FOLDER_MANAGERS',
                },
                { id: 'move_folder', label: i18n.t('permissions:deplacerUnDossier'), apiCode: 'MOVE_FOLDERS' },
                { id: 'archive_folders', label: i18n.t('permissions:archiverDesDossiers'), apiCode: 'ARCHIVE_FOLDERS' },
            ],
        },
        {
            theme: 'Jetons',
            permissions: [
                { id: 'access_token_purchase', label: i18n.t('permissions:accederALaPageAchatDeJetons'), apiCode: 'VIEW_TOKENS' },
                {
                    id: 'view_company_balance',
                    label: i18n.t('permissions:afficherLeSoldeDeJetons'),
                    apiCode: 'VIEW_COMPANY_TOKENS',
                },
            ],
        },
        {
            theme: i18n.t('permissions:equipe'),
            permissions: [
                { id: 'view_team', label: i18n.t('permissions:afficherLEquipe'), apiCode: 'LIST_USERS' },
                {
                    id: 'add_team_members',
                    label: i18n.t('permissions:ajouterDesMembresALEquipe'),
                    apiCode: 'CREATE_COMPANY_MEMBERS',
                },
                {
                    id: 'edit_team_members',
                    label: i18n.t('permissions:modifierDesMembresDeLEquipe'),
                    apiCode: 'UPDATE_COMPANY_MEMBERS',
                },
                {
                    id: 'delete_team_members',
                    label: i18n.t('permissions:supprimerDesMembresDeLEquipe'),
                    apiCode: 'ARCHIVE_COMPANY_MEMBERS',
                },
            ],
        },
        {
            theme: 'Sous-comptes',
            permissions: [
                { id: 'add_subaccounts', label: i18n.t('permissions:ajouterDesSousComptes'), apiCode: 'CREATE_SUBSIDIARIES' },
                {
                    id: 'edit_subaccount_info',
                    label: i18n.t('permissions:modifierLesInformations'),
                    apiCode: 'UPDATE_SUBSIDIARIES',
                },
                {
                    id: 'edit_subaccount_permissions',
                    label: i18n.t('permissions:modifierLesAutorisations'),
                    apiCode: 'UPDATE_SUBSIDIARY_PERMISSIONS',
                },
            ],
        },
        {
            theme: 'Gestion des appareils',
            permissions: [
                {
                    id: 'access_device_management',
                    label: i18n.t('permissions:accederALaPageGestionDesAppareils'),
                    apiCode: 'LIST_DEVICES',
                },
            ],
        },
    ];
}

function getDescendantsPermissionGroups() {
    return [
        {
            theme: 'Photos',
            permissions: [
                { id: 'capture_photos', label: i18n.t('permissions:capturerDesPhotos'), apiCode: 'CAPTURE_CERTIFIED_PHOTOS' },
                { id: 'move_photo', label: i18n.t('permissions:deplacerUnePhoto'), apiCode: 'MOVE_CERTIFIED_PHOTOS' },
            ],
        },
        {
            theme: i18n.t('permissions:procedures'),
            permissions: [
                { id: 'list_procedures', label: i18n.t('permissions:afficherLesProcedures'), apiCode: 'LIST_PROCEDURES' },
                {
                    id: 'modify_other_member_procedure',
                    label: i18n.t('permissions:modifierLaProcedureDUnAutreMembre'),
                    apiCode: 'UPDATE_ANOTHER_MEMBER_PROCEDURE',
                },
                { id: 'complete_procedure', label: i18n.t('permissions:completerUneProcedure'), apiCode: 'UPDATE_PROCEDURE_REPORTS' },
                { id: 'move_report', label: i18n.t('permissions:deplacerUnRapport'), apiCode: 'MOVE_PROCEDURE_REPORTS' },
            ],
        },
        {
            theme: 'Dossiers',
            permissions: [
                {
                    id: 'view_all_folders',
                    label: i18n.t('permissions:consulterTousLesDossiers'),
                    description: i18n.t('permissions:sinonLUtilisateurNeVoitQueLesDossiers'),
                    apiCode: 'LIST_FOLDERS',
                },
                { id: 'add_folders', label: i18n.t('permissions:ajouterDesDossiers'), apiCode: 'CREATE_FOLDERS' },
                { id: 'edit_folders', label: i18n.t('permissions:modifierDesDossiers'), apiCode: 'UPDATE_FOLDERS' },
                {
                    id: 'edit_folder_managers',
                    label: i18n.t('permissions:modifierLesResponsablesDeDossier'),
                    apiCode: 'UPDATE_FOLDER_MANAGERS',
                },
                { id: 'move_folder', label: i18n.t('permissions:deplacerUnDossier'), apiCode: 'MOVE_FOLDERS' },
                { id: 'archive_folders', label: i18n.t('permissions:archiverDesDossiers'), apiCode: 'ARCHIVE_FOLDERS' },
            ],
        },
        {
            theme: 'Jetons',
            permissions: [
                {
                    id: 'view_company_balance',
                    label: i18n.t('permissions:afficherLeSoldeDeJetons'),
                    apiCode: 'VIEW_COMPANY_TOKENS',
                },
                { id: 'send_tokens_subaccount', label: i18n.t('permissions:envoyerDesJetons'), apiCode: 'SEND_TOKENS' },
            ],
        },
        {
            theme: i18n.t('permissions:equipe'),
            permissions: [
                { id: 'view_team', label: i18n.t('permissions:afficherLEquipe'), apiCode: 'LIST_USERS' },
                {
                    id: 'add_team_members',
                    label: i18n.t('permissions:ajouterDesMembresALEquipe'),
                    apiCode: 'CREATE_COMPANY_MEMBERS',
                },
                {
                    id: 'edit_team_members',
                    label: i18n.t('permissions:modifierDesMembresDeLEquipe'),
                    apiCode: 'UPDATE_COMPANY_MEMBERS',
                },
                {
                    id: 'delete_team_members',
                    label: i18n.t('permissions:supprimerDesMembresDeLEquipe'),
                    apiCode: 'ARCHIVE_COMPANY_MEMBERS',
                },
            ],
        },
        {
            theme: 'Sous-comptes',
            permissions: [
                {
                    id: 'edit_subaccount_info',
                    label: i18n.t('permissions:modifierLesInformations'),
                    apiCode: 'UPDATE_SUBSIDIARIES',
                },
                {
                    id: 'edit_subaccount_permissions',
                    label: i18n.t('permissions:modifierLesAutorisations'),
                    apiCode: 'UPDATE_SUBSIDIARY_PERMISSIONS',
                },
            ],
        },
        {
            theme: 'Gestion des appareils',
            permissions: [
                {
                    id: 'access_device_management',
                    label: i18n.t('permissions:accederALaPageGestionDesAppareils'),
                    apiCode: 'LIST_DEVICES',
                },
            ],
        },
    ];
}

function getScopeTabs() {
    return [
        { value: 'SELF', label: i18n.t('permissions:surMonEntreprise') },
        { value: 'DESCENDANTS', label: i18n.t('permissions:surLesSousComptes') },
    ];
}

/* ─── Helpers ─────────────────────────────────────────────── */

function getPermValue(perm, apiRoleName, matrix, scope) {
    if (!perm.apiCode) return false;
    return matrix[apiRoleName]?.[perm.apiCode]?.[scope] ?? false;
}

function makeSavingKey(perm, apiRoleName, scope) {
    return `${apiRoleName}:${perm.apiCode}:${scope}`;
}

function isGroupAllChecked(group, apiRoleName, matrix, isPermDisabled, scope) {
    const enabledPerms = group.permissions.filter((p) => p.apiCode && !isPermDisabled(p));
    if (enabledPerms.length === 0) return false;
    return enabledPerms.every((p) => getPermValue(p, apiRoleName, matrix, scope));
}

function hasAnyEnabledPerm(group, isPermDisabled) {
    return group.permissions.some((p) => p.apiCode && !isPermDisabled(p));
}

/* ─── Table groupée par thème (accordion) ─────────────────── */

function GroupedPermissionsTable({
    groups,
    roles,
    matrix,
    scope,
    savingKey,
    onToggle,
    onGroupToggle,
    savingGroupKey,
    isPermDisabled,
}) {
    const { t } = useTranslation();
    const colCount = roles.length;
    const desktopGridCols =
        {
            1: 'grid-cols-[1fr_140px_32px]',
            2: 'grid-cols-[1fr_repeat(2,_100px)_32px]',
            3: 'grid-cols-[1fr_repeat(3,_100px)_32px]',
            4: 'grid-cols-[1fr_repeat(4,_100px)_32px]',
            5: 'grid-cols-[1fr_repeat(5,_100px)_32px]',
        }[colCount] ?? 'grid-cols-[1fr_repeat(3,_100px)_32px]';

    const allThemes = groups.map((g) => g.theme);

    return (
        <div className="space-y-0">
            {/* Mobile: accordion → cartes par permission */}
            <div className="md:hidden">
                <Accordion type="multiple" defaultValue={allThemes} className="space-y-2">
                    {groups.map((group) => {
                        const groupKey = `group:${group.theme}`;
                        return (
                            <AccordionItem
                                key={group.theme}
                                value={group.theme}
                                className="bg-surface rounded-xl border border-border overflow-hidden border-b"
                            >
                                <AccordionTrigger className="px-4 py-3 font-normal text-foreground hover:no-underline">
                                    {group.theme}
                                </AccordionTrigger>
                                <AccordionContent className="px-6 pb-4">
                                    {/* Tout cocher/décocher par rôle */}
                                    {hasAnyEnabledPerm(group, isPermDisabled) && (
                                        <div className="bg-muted/40 rounded-lg p-3 border border-border mb-3">
                                            <div className="text-xs font-semibold mb-2">{t('permissions:toutCocherDecocher')}</div>
                                            <div className="space-y-2">
                                                {roles.map((role) => {
                                                    const isSaving = savingGroupKey === `${groupKey}:${role.apiRole}`;
                                                    const allChecked = isGroupAllChecked(
                                                        group,
                                                        role.apiRole,
                                                        matrix,
                                                        isPermDisabled,
                                                        scope,
                                                    );
                                                    return (
                                                        <label
                                                            key={role.key}
                                                            className="flex items-center justify-between cursor-pointer"
                                                        >
                                                            <span className="text-[13px]">{role.label}</span>
                                                            {isSaving ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <Checkbox
                                                                    checked={allChecked}
                                                                    onCheckedChange={() =>
                                                                        onGroupToggle(group, role, !allChecked)
                                                                    }
                                                                />
                                                            )}
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    <div className="space-y-3">
                                        {group.permissions.map((perm) => {
                                            const disabled = isPermDisabled(perm);
                                            return (
                                                <div
                                                    key={perm.id}
                                                    className={cn(
                                                        'bg-card rounded-lg p-3 border border-border',
                                                        disabled && 'opacity-50',
                                                    )}
                                                >
                                                    <div className="mb-3">
                                                        <div className="text-sm text-foreground font-medium">
                                                            {perm.label}
                                                        </div>
                                                        {perm.description && (
                                                            <div className="text-xs mt-0.5">{perm.description}</div>
                                                        )}
                                                    </div>
                                                    <div className="space-y-3">
                                                        {roles.map((role) => {
                                                            const key = perm.apiCode
                                                                ? makeSavingKey(perm, role.apiRole, scope)
                                                                : null;
                                                            const isSaving = key && savingKey === key;
                                                            return (
                                                                <label
                                                                    key={role.key}
                                                                    className={cn(
                                                                        'flex items-center justify-between',
                                                                        !disabled && 'cursor-pointer',
                                                                    )}
                                                                >
                                                                    <span className="text-[13px]">{role.label}</span>
                                                                    {isSaving ? (
                                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                                    ) : (
                                                                        <Checkbox
                                                                            checked={getPermValue(
                                                                                perm,
                                                                                role.apiRole,
                                                                                matrix,
                                                                                scope,
                                                                            )}
                                                                            disabled={disabled}
                                                                            onCheckedChange={
                                                                                !disabled
                                                                                    ? () => onToggle(perm, role)
                                                                                    : undefined
                                                                            }
                                                                        />
                                                                    )}
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                        );
                    })}
                </Accordion>
            </div>

            {/* Desktop: tableau avec accordion par groupe */}
            <div className="hidden md:block bg-card rounded-xl border border-border overflow-hidden">
                {/* En-tête fixe */}
                <div className="border-b border-border bg-muted">
                    <div className={cn('grid gap-3 px-5 py-3.5', desktopGridCols)}>
                        <div className="text-[13px] text-foreground font-semibold">{t('permissions:autorisation')}</div>
                        {roles.map((role) => (
                            <div key={role.key} className="text-[13px] text-foreground font-semibold text-center">
                                {role.label}
                            </div>
                        ))}
                        <div />
                    </div>
                </div>
                {/* Groupes */}
                <Accordion type="multiple" defaultValue={allThemes}>
                    {groups.map((group, groupIdx) => {
                        const groupKey = `group:${group.theme}`;
                        return (
                            <AccordionItem
                                key={group.theme}
                                value={group.theme}
                                className={cn(groupIdx < groups.length - 1 && 'border-b')}
                            >
                                <div
                                    className={cn(
                                        'grid gap-3 items-center bg-muted/30 hover:bg-muted/50 px-5',
                                        desktopGridCols,
                                    )}
                                >
                                    <AccordionPrimitive.Trigger className="py-3 text-sm text-foreground text-left cursor-pointer">
                                        {group.theme}
                                    </AccordionPrimitive.Trigger>
                                    {roles.map((role) => {
                                        const isSaving = savingGroupKey === `${groupKey}:${role.apiRole}`;
                                        const allChecked = isGroupAllChecked(
                                            group,
                                            role.apiRole,
                                            matrix,
                                            isPermDisabled,
                                            scope,
                                        );
                                        const groupHasEnabled = hasAnyEnabledPerm(group, isPermDisabled);
                                        return (
                                            <div key={role.key} className="flex items-center justify-center">
                                                {isSaving ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <Checkbox
                                                        checked={allChecked}
                                                        disabled={!groupHasEnabled}
                                                        onCheckedChange={
                                                            groupHasEnabled
                                                                ? () => onGroupToggle(group, role, !allChecked)
                                                                : undefined
                                                        }
                                                        className={!groupHasEnabled ? 'cursor-not-allowed' : ''}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                    <AccordionPrimitive.Trigger className="flex items-center justify-center cursor-pointer [&[data-state=open]>svg]:rotate-180">
                                        <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                                    </AccordionPrimitive.Trigger>
                                </div>
                                <AccordionContent className="p-0">
                                    {group.permissions.map((perm, index) => {
                                        const isLastPerm = index === group.permissions.length - 1;
                                        const disabled = isPermDisabled(perm);
                                        return (
                                            <div
                                                key={perm.id}
                                                className={cn(
                                                    'grid gap-3 pl-10 pr-5 py-3.5 hover:bg-surface transition-colors',
                                                    desktopGridCols,
                                                    !isLastPerm && 'border-b border-border',
                                                    disabled && 'bg-muted opacity-50',
                                                )}
                                            >
                                                <div className="flex flex-col justify-center">
                                                    <span className="text-sm text-foreground">{perm.label}</span>
                                                    {perm.description && (
                                                        <span className="text-xs">{perm.description}</span>
                                                    )}
                                                </div>
                                                {roles.map((role) => {
                                                    const key = perm.apiCode
                                                        ? makeSavingKey(perm, role.apiRole, scope)
                                                        : null;
                                                    const isSaving = key && savingKey === key;
                                                    return (
                                                        <div
                                                            key={role.key}
                                                            className="flex items-center justify-center"
                                                        >
                                                            {isSaving ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <Checkbox
                                                                    checked={getPermValue(
                                                                        perm,
                                                                        role.apiRole,
                                                                        matrix,
                                                                        scope,
                                                                    )}
                                                                    disabled={disabled}
                                                                    onCheckedChange={
                                                                        !disabled
                                                                            ? () => onToggle(perm, role)
                                                                            : undefined
                                                                    }
                                                                    className={disabled ? 'cursor-not-allowed' : ''}
                                                                />
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                                <div />
                                            </div>
                                        );
                                    })}
                                </AccordionContent>
                            </AccordionItem>
                        );
                    })}
                </Accordion>
            </div>
        </div>
    );
}

/* ─── Page principale ─────────────────────────────────────── */

export default function PermissionsPage() {
    const { t } = useTranslation();
    const SCOPE_TABS = getScopeTabs();
    const DESCENDANTS_PERMISSION_GROUPS = getDescendantsPermissionGroups();
    const SELF_PERMISSION_GROUPS = getSelfPermissionGroups();
    const ROLES = getRoles();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isSuperAdmin = user?.role === 'admin';

    const visibleRoles = useMemo(() => {
        const allowed = VISIBLE_API_ROLES[user?.role] ?? [];
        return ROLES.filter((r) => allowed.includes(r.apiRole));
    }, [user?.role, t]);

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [matrix, setMatrix] = useState({});
    const [userPermissions, setUserPermissions] = useState({});
    const [savingKey, setSavingKey] = useState(null);
    const [savingGroupKey, setSavingGroupKey] = useState(null);
    const [activeScope, setActiveScope] = useState('SELF');

    const matrixRef = useRef(matrix);
    useEffect(() => {
        matrixRef.current = matrix;
    }, [matrix]);

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            setError('');
            try {
                const res = await horodatyApi.managerGetPermissions();
                const data = res.data?.data;
                console.log('[PermissionsPage] API response matrix:', data?.matrix);
                console.log('[PermissionsPage] API response user_permissions:', data?.user_permissions);
                setMatrix(data?.matrix ?? {});
                setUserPermissions(data?.user_permissions ?? {});
            } catch {
                setError('Erreur lors du chargement des permissions.');
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, []);

    /** Une permission est désactivée si le manager connecté ne la possède pas lui-même (Super Admin peut tout modifier) */
    const isPermDisabled = useCallback(
        (perm) => {
            if (!perm.apiCode) return true;
            if (isSuperAdmin) return false;
            const scopes = userPermissions[perm.apiCode];
            if (!scopes) return true;
            return !scopes.includes(activeScope);
        },
        [userPermissions, activeScope, isSuperAdmin],
    );

    const handleToggle = useCallback(
        async (perm, role) => {
            if (!perm.apiCode) return;
            const apiRole = role.apiRole;
            const scope = activeScope;
            const key = makeSavingKey(perm, apiRole, scope);
            const currentValue = matrixRef.current[apiRole]?.[perm.apiCode]?.[scope] ?? false;
            const newValue = !currentValue;

            setMatrix((prev) => {
                const next = JSON.parse(JSON.stringify(prev));
                if (!next[apiRole]) next[apiRole] = {};
                if (!next[apiRole][perm.apiCode]) next[apiRole][perm.apiCode] = { SELF: false, DESCENDANTS: false };
                next[apiRole][perm.apiCode][scope] = newValue;
                return next;
            });

            setSavingKey(key);
            try {
                await horodatyApi.managerTogglePermission(apiRole, perm.apiCode, scope, newValue);
            } catch {
                toast.error(t('permissions:erreurLorsDeLaSauvegarde'));
                setMatrix((prev) => {
                    const next = JSON.parse(JSON.stringify(prev));
                    if (next[apiRole]?.[perm.apiCode]) next[apiRole][perm.apiCode][scope] = currentValue;
                    return next;
                });
            } finally {
                setSavingKey(null);
            }
        },
        [activeScope, t],
    );

    const handleGroupToggle = useCallback(
        async (group, role, newValue) => {
            const apiRole = role.apiRole;
            const groupKey = `group:${group.theme}:${apiRole}`;

            const permsToToggle = group.permissions
                .filter((p) => p.apiCode && !isPermDisabled(p))
                .map((p) => ({ permission: p.apiCode, scope: activeScope, value: newValue }));

            if (permsToToggle.length === 0) return;

            const snapshot = JSON.parse(JSON.stringify(matrixRef.current));

            setMatrix((prev) => {
                const next = JSON.parse(JSON.stringify(prev));
                if (!next[apiRole]) next[apiRole] = {};
                for (const p of permsToToggle) {
                    if (!next[apiRole][p.permission]) next[apiRole][p.permission] = { SELF: false, DESCENDANTS: false };
                    next[apiRole][p.permission][p.scope] = newValue;
                }
                return next;
            });

            setSavingGroupKey(groupKey);
            try {
                await horodatyApi.managerBatchTogglePermissions(apiRole, permsToToggle);
            } catch {
                toast.error(t('permissions:erreurLorsDeLaSauvegardeGroupee'));
                setMatrix(snapshot);
            } finally {
                setSavingGroupKey(null);
            }
        },
        [isPermDisabled, activeScope, t],
    );

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-secondary" />
            </div>
        );
    }

    return (
        <>
            <MobileHeaderNavigation title={t('permissions:autorisations')} />

            <PageHeader>
                <div className="flex items-center gap-2">
                    <BackButton onClick={() => navigate('/settings/company')} />
                    <div>
                        <h1 className="text-xl font-semibold text-foreground">{t('permissions:autorisations')}</h1>
                        <p className="text-[13px] mt-0.5">{t('permissions:configurezLesPermissionsDeVosCollaborateurs')}</p>
                    </div>
                </div>
            </PageHeader>
            <div className="flex-1 min-h-0 overflow-auto p-4 md:p-6 pb-24 space-y-6">
                {/* Header */}

                <div className="md:max-w-[62.5%] md:min-w-[728px] mx-auto space-y-6">
                    {error && <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg">{error}</div>}

                    {/* Scope tabs */}
                    <div className="bg-surface rounded-xl p-1 flex gap-2 w-full max-w-[400px]">
                        {SCOPE_TABS.map((tab) => (
                            <Button
                                key={tab.value}
                                variant="plain"
                                color="neutral"
                                size="xlg"
                                onClick={() => setActiveScope(tab.value)}
                                className={cn(
                                    'flex-1 text-[14px] font-medium',
                                    activeScope === tab.value
                                        ? 'bg-card text-foreground shadow-sm hover:bg-card'
                                        : 'text-muted-foreground hover:bg-card',
                                )}
                            >
                                {tab.label}
                            </Button>
                        ))}
                    </div>

                    {/* Info descendants */}
                    {activeScope === 'DESCENDANTS' && (
                        <div className="bg-card rounded-xl p-4 border border-border">
                            <p className="text-[14px] text-muted-foreground">
                                {t('permissions:ceTableauRegitLesAutorisationsDeVotrePropre')}
                            </p>
                        </div>
                    )}

                    {/* Permissions matrix */}
                    <GroupedPermissionsTable
                        groups={activeScope === 'SELF' ? SELF_PERMISSION_GROUPS : DESCENDANTS_PERMISSION_GROUPS}
                        roles={visibleRoles}
                        matrix={matrix}
                        scope={activeScope}
                        savingKey={savingKey}
                        onToggle={handleToggle}
                        onGroupToggle={handleGroupToggle}
                        savingGroupKey={savingGroupKey}
                        isPermDisabled={isPermDisabled}
                    />
                    <div className="bg-muted rounded-xl p-4 border border-border">
                        <p className="text-[14px] text-muted-foreground">
                            {t('permissions:toutSousCompteCreeMaintenantHeriteraParDefaut')}
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
}
